import React from 'react';
import ExternalLink from '~/components/ExternalLink';
import { getAppContext } from '~/lib/AppContext';

type ActionErrorMessageProps = {
    /** The action's customer-safe failure reason, when the API supplied one. */
    errorMessage?: string | null;
    /** Class for the contact-support link; set to "alert-link" when rendered inside an <Alert> (see PanelSite.md → "Links Inside Alerts"). */
    linkClassName?: string;
};

/**
 * Body text shown when an action fails. Renders the customer-safe {@link errorMessage} when the API
 * supplied one, otherwise a generic failure message; both variants keep a contact-support link.
 * Shared by the ActionUI toast and the ActionMonitor alert so the two error surfaces stay in sync.
 */
export default function ActionErrorMessage({ errorMessage, linkClassName }: ActionErrorMessageProps) {
    const contactSupport = (
        <ExternalLink href={`${getAppContext().config.wwwSite}/contact-us`} className={linkClassName}>
            contact support
        </ExternalLink>
    );

    if (errorMessage) {
        // Allowlisted FriendlyMessage implementers are not required to terminate their message, so
        // guarantee sentence punctuation before the follow-up to avoid a run-on with the next sentence.
        const trimmed = errorMessage.trim();
        const punctuated = /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
        return (
            <span>
                {punctuated} If you need further help, please {contactSupport}.
            </span>
        );
    }

    return (
        <span>
            Sorry, an error occurred that prevented your request from being completed. Please {contactSupport} for help
            in resolving the problem.
        </span>
    );
}
